import { FlexProps } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FC } from 'react';
import { MontyPeek } from '../../assets/images';
import { MBox, MFlex, MImage } from '../Monetize/chakra';

/**
 * Monty peeking up from the bottom edge of whatever contains him. The SVG's
 * flat bottom sits on its viewBox edge, so the parent must clip
 * (`overflow="hidden"`) and bottom-align this image for the peek to read.
 *
 * Decorative (`alt=""`): every call site has an adjacent name or `aria-label`.
 */
export const MontyPeekImage: FC = () => (
  <MImage src={MontyPeek} alt="" width="100%" height="auto" draggable={false} />
);

const peek = keyframes`
  0%, 100% { transform: translateY(0); }
  45%, 55% { transform: translateY(22%); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 2px 8px rgba(140, 50, 255, 0.25); }
  50% { box-shadow: 0 0 0 3px rgba(140, 50, 255, 0.18), 0 2px 12px rgba(140, 50, 255, 0.35); }
`;

export interface MontyAvatarProps extends Omit<FlexProps, 'children'> {
  boxSize: FlexProps['boxSize'];
  /** Monty ducks and peeks back up while he works on a reply. */
  isWorking?: boolean;
}

/** Rounded tile with Monty peeking out — the chat assistant's avatar. */
export const MontyAvatar: FC<MontyAvatarProps> = ({
  boxSize,
  isWorking,
  ...rest
}) => (
  <MFlex
    alignItems="flex-end"
    justifyContent="center"
    boxSize={boxSize}
    borderRadius="lg"
    overflow="hidden"
    flexShrink={0}
    bg="tMagenta.50"
    boxShadow="0 2px 8px rgba(140, 50, 255, 0.25)"
    animation={isWorking ? `${glow} 1.8s ease-in-out infinite` : undefined}
    _motionReduce={{ animation: 'none' }}
    {...rest}
  >
    <MBox
      width="100%"
      animation={isWorking ? `${peek} 1.8s ease-in-out infinite` : undefined}
      _motionReduce={{ animation: 'none' }}
    >
      <MontyPeekImage />
    </MBox>
  </MFlex>
);
